import { useState, type MutableRefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import type { GridApi } from 'ag-grid-community'
import { extractAndDisplayError } from '../../../../utils/errorUtils'
import { useSupplierReconciliation } from '../../../../api/useSupplierReconciliation'
import type { Match } from '@api/useSupplierReconciliation/types/match'
import type { InvoiceFormValues } from '@api/useSupplierReconciliation/types/invoice'
import dayjs from 'dayjs'

interface UseMatchActionsParams {
  onRefetch?: () => void
  gridAPIRef?: MutableRefObject<GridApi<Match> | null>
}

export function useMatchActions(params: UseMatchActionsParams = {}) {
  const { onRefetch, gridAPIRef } = params
  const queryClient = useQueryClient()

  // Get API hooks from useSupplierReconciliation
  const {
    useSetMatchHold,
    useApproveMatch,
    useUnapproveMatch,
    useVoidInvoice,
    useUnvoidInvoice,
    useCreateManualInvoice,
    useLinkMatch,
  } = useSupplierReconciliation()

  // Initialize mutations

  const unapproveMatchMutation = useUnapproveMatch()
  const approveMatchMutation = useApproveMatch()
  const waitingOnSupplierMutation = useSetMatchHold()
  const voidInvoiceMutation = useVoidInvoice()
  const unvoidInvoiceMutation = useUnvoidInvoice()
  const createInvoiceMutation = useCreateManualInvoice()
  const linkMatchMutation = useLinkMatch()

  // Real bulk mutations using actual API endpoints
  const bulkApproveMutation = useApproveMatch()
  const bulkHoldMutation = useSetMatchHold()

  // --- Action Handlers --- //

  const handleMerge = async (
    matchId: string,
    links: {
      invoices: Array<{ invoice_number: string; invoice_id: string | null }>
      bols: Array<{ bol_number: string; bol_id: string | null }>
    },
    isUnmatch?: boolean
  ) => {
    const invoiceCount = links.invoices?.length || 0
    const bolCount = links.bols?.length || 0

    try {
      // Use the new link endpoint structure
      await linkMatchMutation.mutateAsync({
        body: {
          match_id: matchId,
          links: links,
        },
      })

      message.success(
        isUnmatch
          ? 'Successfully unmatched items'
          : `Successfully merged ${invoiceCount} invoice${invoiceCount > 1 ? 's' : ''} and ${bolCount} BOL${bolCount > 1 ? 's' : ''}`
      )

      // Refresh row heights to accommodate new invoices/BOLs
      if (gridAPIRef?.current) {
        setTimeout(() => {
          gridAPIRef.current?.resetRowHeights()
        }, 100) // Small delay to allow cache updates to complete
      }
    } catch (error) {
      extractAndDisplayError(error, 'Failed to merge matches')
      throw error
    }
  }

  const handleBulkApprove = async (rows: Match[], reason?: string) => {
    // Use bulk approve API endpoint
    const matchIds = rows.filter((match) => match.status !== 'approved').map((match) => match.id)

    if (matchIds.length === 0) {
      message.warning('No matches to approve')
      return
    }

    try {
      await bulkApproveMutation.mutateAsync({
        body: {
          match_ids: matchIds,
          approval_reason: reason,
        },
      })

      message.success(`Successfully approved ${matchIds.length} match${matchIds.length > 1 ? 'es' : ''}`)
      // onRefetch removed - using optimistic updates instead
    } catch (error) {
      extractAndDisplayError(error, 'Failed to approve matches')
      throw error
    }
  }

  const handleBulkHold = async (rows: Match[], reason?: string) => {
    // Use bulk hold API endpoint
    const matchIds = rows.filter((match) => match.status !== 'hold').map((match) => match.id)

    if (matchIds.length === 0) {
      message.warning('No matches to mark as on hold')
      return
    }

    try {
      await bulkHoldMutation.mutateAsync({
        body: {
          match_ids: matchIds,
          hold: true,
          hold_reason: reason,
        },
      })

      message.success(`Successfully marked ${matchIds.length} match${matchIds.length > 1 ? 'es' : ''} as on hold`)
      // onRefetch removed - using optimistic updates instead
    } catch (error) {
      extractAndDisplayError(error, 'Failed to mark matches as on hold')
      throw error
    }
  }

  const handleIndividualUnapprove = async (matchId: string) => {
    await unapproveMatchMutation.mutateAsync({
      body: {
        match_ids: [matchId],
      },
    })
  }

  const handleIndividualApprove = async (matchId: string, reason?: string) => {
    await approveMatchMutation.mutateAsync({
      body: {
        match_ids: [matchId],
        approval_reason: reason,
      },
    })
  }

  const handleIndividualHold = async (matchId: string, reason: string) => {
    await waitingOnSupplierMutation.mutateAsync({
      body: {
        match_ids: [matchId],
        hold: true,
        hold_reason: reason,
      },
    })
  }

  const handleIndividualUnhold = async (matchId: string) => {
    await waitingOnSupplierMutation.mutateAsync({
      body: {
        match_ids: [matchId],
        hold: false,
      },
    })
  }

  const handleVoidInvoice = async (invoiceId: string) => {
    await voidInvoiceMutation.mutateAsync({
      params: {
        query: {
          invoice_id: invoiceId,
        },
      },
    })
  }

  const handleUnvoidInvoice = async (invoiceId: string) => {
    await unvoidInvoiceMutation.mutateAsync({
      params: {
        query: {
          invoice_id: invoiceId,
        },
      },
    })
  }

  const handleCreateInvoice = async (invoiceData: { body: unknown; bolNumber?: string }) => {
    try {
      const response = await createInvoiceMutation.mutateAsync(invoiceData)

      // Auto-link BOL if user entered one during invoice creation
      if (invoiceData.bolNumber && (response as any)?.id) {
        try {
          await linkMatchMutation.mutateAsync({
            body: {
              match_id: (response as any).id,
              links: {
                bols: [
                  {
                    bol_number: invoiceData.bolNumber,
                    bol_id: null,
                  },
                ],
              },
            },
          })
          // Success message handled by mutation
        } catch (linkError) {
          console.warn('Failed to auto-link BOL number to match:', linkError)
          extractAndDisplayError(linkError, 'Invoice created but failed to auto-link BOL')
        }
      }

      // onRefetch removed - using optimistic updates instead
    } catch (error) {
      // Error message handled by mutation
      throw error
    }
  }

  // Helper function to convert InvoiceCreateRequest to InvoiceFormValues
  const convertToInvoiceFormValues = (request: {
    products?: Array<{ total: number }>
    taxes?: Array<{ amount: number }>
    [key: string]: unknown
  }): InvoiceFormValues => {
    return {
      invoiceNumber: request.invoice_number,
      invoiceDate: dayjs(request.invoice_date),
      invoiceDue: dayjs(request.invoice_due_date),
      bolNumber: request.bol_number || '',
      terminal: request.terminal_id,
      supplier: request.supplier_id,
      billingBy: request.billing_method || 'standard',
      site: request.site_id || '',
      multipleSites: false,
      taxAuthority: request.tax_authority || 'state',
      products: request.products || [],
      taxes: request.taxes || [],
      productTotal: request.products?.reduce((sum: number, p: { total: number }) => sum + p.total, 0) || 0,
      taxTotal: request.taxes?.reduce((sum: number, t: { amount: number }) => sum + t.amount, 0) || 0,
      grandTotal:
        (request.products?.reduce((sum: number, p: { total: number }) => sum + p.total, 0) || 0) +
        (request.taxes?.reduce((sum: number, t: { amount: number }) => sum + t.amount, 0) || 0),
    }
  }

  // --- Return Hook Interface --- //

  return {
    // Individual mutations
    approveMatchMutation,
    waitingOnSupplierMutation,
    voidInvoiceMutation,
    unvoidInvoiceMutation,
    createInvoiceMutation,
    linkMatchMutation,

    // Bulk mutations
    bulkApproveMutation,
    bulkHoldMutation,

    // Action handlers
    handleMerge,
    handleBulkApprove,
    handleBulkHold,
    handleIndividualUnapprove,
    handleIndividualApprove,
    handleIndividualHold,
    handleIndividualUnhold,
    handleVoidInvoice,
    handleUnvoidInvoice,
    handleCreateInvoice,
  }
}
